import { ReactNode } from "react"
import { Link, usePage } from "@inertiajs/react"
import { PromotionShowProps } from "@/types/types"
import Markdown from "markdown-to-jsx"
import DefaultLayout from "@/layouts/DefaultLayout"
import Container from "@/components/Container"
import CTA from "@/components/CTA"
import CustomLink from "@/components/CustomLink"
import Tweets from "@/components/Tweets"
import clsx from "clsx"
import { localeDetailFor, promotionCopyFor } from "@/utils/promotionLocale"

const PromotionPage = () => {
  const { promotion, selected_locale: selectedLocale } =
    usePage<PromotionShowProps>().props
  const translation = promotion.translations.find(
    ({ locale }) => locale === selectedLocale
  ) ??
    promotion.translations[0] ?? {
      id: 0,
      locale: promotion.default_locale,
      title: promotion.title,
      description: promotion.description,
      content: promotion.content,
    }
  const copy = promotionCopyFor(selectedLocale)

  return (
    <>
      <header className="border-b border-stone-200 bg-stone-50">
        <Container maxWidthClassName="max-w-5xl" className="py-10 sm:py-14">
          <nav aria-label={copy.languageSelector} className="mb-8">
            <div className="inline-flex max-w-full overflow-x-auto rounded-lg border border-stone-300 bg-white p-1 shadow-xs">
              {promotion.translations.map(({ locale }) => {
                const details = localeDetailFor(locale)
                const active = locale === selectedLocale

                return (
                  <Link
                    key={locale}
                    href={`/p/${promotion.slug}?lang=${locale}`}
                    preserveScroll
                    aria-label={details.label}
                    aria-current={active ? "page" : undefined}
                    className={clsx(
                      "flex h-9 shrink-0 items-center justify-center rounded-md px-3 text-sm font-semibold transition-colors sm:min-w-24",
                      active
                        ? "bg-stone-900 text-white"
                        : "text-stone-600 hover:bg-stone-100 hover:text-stone-950"
                    )}
                  >
                    <span className="sm:hidden" aria-hidden="true">
                      {details.shortLabel}
                    </span>
                    <span className="hidden sm:inline" aria-hidden="true">
                      {details.label}
                    </span>
                  </Link>
                )
              })}
            </div>
          </nav>

          <div className="max-w-3xl">
            <div className="mb-4 flex flex-wrap items-center gap-2 text-sm font-semibold">
              <span className="text-chessdrop-700">{promotion.hashtag}</span>
              <span className="text-stone-300" aria-hidden="true">
                /
              </span>
              <span className="text-stone-500">{copy.participation}</span>
            </div>
            <h1 className="text-3xl leading-tight font-bold text-stone-950 sm:text-4xl">
              {translation.title}
            </h1>
            <p className="mt-4 max-w-2xl text-lg leading-8 text-stone-600">
              {translation.description}
            </p>
          </div>
        </Container>
      </header>

      <Tweets locale={selectedLocale} />

      <Container
        tag="section"
        maxWidthClassName="max-w-3xl"
        className="py-12 sm:py-16"
      >
        <article className="prose prose-stone sm:prose-lg mx-auto max-w-none">
          <h2 className="text-2xl font-bold text-stone-950 sm:text-3xl">
            {copy.rules}
          </h2>
          <Markdown
            options={{
              forceBlock: true,
              overrides: {
                a: { component: CustomLink },
                script: () => null,
                iframe: () => null,
                object: () => null,
                style: () => null,
              },
            }}
          >
            {translation.content}
          </Markdown>
        </article>
      </Container>

      <CTA />
    </>
  )
}

export default PromotionPage

PromotionPage.layout = (page: ReactNode) => (
  <DefaultLayout>{page}</DefaultLayout>
)
